import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // `hover:bg-primary-hover` is ours, not the CLI's `/90`: see the
        // contract in components.css. Re-apply if this file is regenerated.
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover hover:text-primary-hover-foreground',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20',
        // `text-foreground` is the one thing here the CLI does not write, and
        // it is not branding — upstream states the fill and leaves the label to
        // whatever it inherits, which holds only while the button stands on the
        // page. On a dark section the inherited colour is the near-white
        // `--on-panel` and the label disappears into its own off-white pill. It
        // is the pair to `bg-background`, so the two move together and neither
        // is repointed on the panel. Re-add it if this file is regenerated.
        outline:
          'border bg-background text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // Not the CLI's heights: the common 32 / 40 / 48 scale, so `default`
        // and `lg` clear the 44px touch target and `lg` meets the 48px one.
        // Re-apply if this file is regenerated.
        default: 'h-10 px-5 py-2 has-[>svg]:px-4',
        sm: 'h-8 rounded-button gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: "h-12 rounded-button px-8 text-base has-[>svg]:px-6 [&_svg:not([class*='size-'])]:size-5",
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
